import { useLiveQuery } from "@hq/client-react";
import type { SearchResult } from "@hq/local-store";
import { Command, Dialog, Kbd, useShortcut, type Platform } from "@z/ui";
import {
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { matchActions, type Action, type ActionContext, type ActionRegistry } from "./registry";
import { shortcutChords } from "./shortcuts";
import { useGlobalShortcuts } from "./useGlobalShortcuts";

export type PaletteMode = "all" | "actions" | "notes";

export type ParsedPaletteQuery = { mode: PaletteMode; text: string };

/** `>` lists actions only, `#` is the note quick switcher, anything else searches everything. */
export function parsePaletteQuery(raw: string): ParsedPaletteQuery {
  if (raw.startsWith(">")) return { mode: "actions", text: raw.slice(1).trim() };
  if (raw.startsWith("#")) return { mode: "notes", text: raw.slice(1).trim() };
  return { mode: "all", text: raw.trim() };
}

/** Removes the `[` `]` hit markers a store puts in a snippet. */
export function plainSnippet(snippet: string): string {
  return snippet.replace(/\[([^\]]*)\]/g, "$1");
}

export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_LIMIT = 8;
export const HINT_TEXT = "Type > for commands, # for notes";

/** Action ids the app registers so the palette can open search results. */
export const OPEN_NOTE_ACTION = "notes.open";
export const OPEN_TASK_ACTION = "tasks.open";
export const TOGGLE_ACTION = "palette.toggle";

export type CommandPaletteProps = {
  registry: ActionRegistry;
  getContext: () => ActionContext;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Overrides platform detection for shortcut labels (tests). */
  platform?: Platform;
};

function reportFailure(id: string) {
  return (error: unknown): void => {
    console.error(`Action "${id}" failed`, error);
  };
}

function ShortcutKeys({
  shortcut,
  platform,
}: {
  shortcut: string;
  platform: Platform | undefined;
}) {
  const chords = shortcutChords(shortcut);
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1 pl-3">
      {chords.map((chord, index) => (
        // A sequence can repeat a chord ("g g"); position keeps the key unique.
        <Kbd
          key={`${chord}-${index}`}
          keys={chord}
          {...(platform === undefined ? {} : { platform })}
        />
      ))}
    </span>
  );
}

function ActionItem({
  action,
  platform,
  onRun,
}: {
  action: Action;
  platform: Platform | undefined;
  onRun: (action: Action) => void;
}) {
  const Icon = action.icon;
  return (
    <Command.Item
      value={`action:${action.id}`}
      {...(action.keywords === undefined ? {} : { keywords: action.keywords })}
      onSelect={() => onRun(action)}
    >
      {Icon === undefined ? (
        <span className="size-4 shrink-0" aria-hidden="true" />
      ) : (
        <Icon className="size-4" />
      )}
      <span className="min-w-0 flex-1 truncate">{action.title}</span>
      {action.shortcut !== undefined && (
        <ShortcutKeys shortcut={action.shortcut} platform={platform} />
      )}
    </Command.Item>
  );
}

function ResultItem({
  kind,
  hit,
  onOpen,
}: {
  kind: "note" | "task";
  hit: SearchResult;
  onOpen: (kind: "note" | "task", id: string) => void;
}) {
  const snippet = plainSnippet(hit.snippet);
  return (
    <Command.Item value={`${kind}:${hit.id}`} onSelect={() => onOpen(kind, hit.id)}>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{hit.title}</span>
        {snippet !== "" && snippet !== hit.title && (
          <span className="truncate text-xs text-fg-muted">{snippet}</span>
        )}
      </span>
    </Command.Item>
  );
}

/** Groups visible actions by heading, preserving order of first appearance. */
function groupActions(actions: readonly Action[]): { heading: string; actions: Action[] }[] {
  const groups: { heading: string; actions: Action[] }[] = [];
  const byHeading = new Map<string, Action[]>();
  for (const action of actions) {
    const heading = action.group ?? "Actions";
    let bucket = byHeading.get(heading);
    if (bucket === undefined) {
      bucket = [];
      byHeading.set(heading, bucket);
      groups.push({ heading, actions: bucket });
    }
    bucket.push(action);
  }
  return groups;
}

/**
 * The palette body owns the query. It mounts with the dialog content and unmounts when the
 * dialog closes, so every open starts with an empty query and fresh search subscriptions.
 */
function PaletteBody({ registry, getContext, onOpenChange, platform }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const { mode, text } = parsePaletteQuery(query);
  // The deferred value is the debounce: typing stays responsive while search catches up.
  const deferredText = useDeferredValue(text);

  const snapshot = useSyncExternalStore(registry.subscribe, registry.snapshot, registry.snapshot);
  const ctx = getContext();
  const visibleActions = snapshot.actions.filter((a) => a.when === undefined || a.when(ctx));
  const showActions = mode !== "notes";
  const matches = showActions ? matchActions(visibleActions, text, snapshot.recent) : [];

  const wantNotes = mode !== "actions" && deferredText.length >= SEARCH_MIN_LENGTH;
  const wantTasks = mode === "all" && deferredText.length >= SEARCH_MIN_LENGTH;
  const noteQuery = wantNotes ? deferredText : null;
  const taskQuery = wantTasks ? deferredText : null;
  const notes = useLiveQuery(
    ["notes"],
    (store) =>
      noteQuery === null
        ? Promise.resolve<SearchResult[]>([])
        : store.search.notes(noteQuery, { limit: SEARCH_LIMIT }),
    [noteQuery],
  );
  const tasks = useLiveQuery(
    ["tasks"],
    (store) =>
      taskQuery === null
        ? Promise.resolve<SearchResult[]>([])
        : store.search.tasks(taskQuery, { limit: SEARCH_LIMIT }),
    [taskQuery],
  );
  const noteHits = noteQuery === null ? [] : (notes.data ?? []);
  const taskHits = taskQuery === null ? [] : (tasks.data ?? []);

  const close = (): void => onOpenChange(false);

  const runAction = (action: Action): void => {
    const promise = registry.run(action.id, getContext());
    close();
    promise.catch(reportFailure(action.id));
  };

  const openResult = (kind: "note" | "task", id: string): void => {
    const actionId = kind === "note" ? OPEN_NOTE_ACTION : OPEN_TASK_ACTION;
    const resultCtx: ActionContext =
      kind === "note" ? { ...getContext(), noteId: id } : { ...getContext(), taskId: id };
    const promise = registry.run(actionId, resultCtx);
    close();
    promise.catch(reportFailure(actionId));
  };

  const actionGroups =
    text === ""
      ? (() => {
          // Opening the palette runs palette.toggle; listing it under Recent would be noise.
          const recentIds = new Set(snapshot.recent.filter((id) => id !== TOGGLE_ACTION));
          const recent = matches.filter((m) => recentIds.has(m.action.id)).map((m) => m.action);
          const rest = matches.filter((m) => !recentIds.has(m.action.id)).map((m) => m.action);
          const groups = groupActions(rest);
          return recent.length > 0 ? [{ heading: "Recent", actions: recent }, ...groups] : groups;
        })()
      : matches.length > 0
        ? [{ heading: "Actions", actions: matches.map((m) => m.action) }]
        : [];

  const placeholder =
    mode === "actions"
      ? "Run a command…"
      : mode === "notes"
        ? "Open a note…"
        : "Search actions, notes, tasks…";

  return (
    <Command.Root
      value={query}
      onValueChange={setQuery}
      onEscape={close}
      className="bg-transparent"
    >
      <Command.Input placeholder={placeholder} aria-label="Command palette" autoFocus />
      <Command.List>
        <Command.Empty>
          <p>{text === "" ? "Nothing here yet" : "No results"}</p>
          <p className="mt-1 text-xs text-fg-faint">{HINT_TEXT}</p>
        </Command.Empty>
        {actionGroups.map((group) => (
          <Command.Group key={group.heading} heading={group.heading}>
            {group.actions.map((action) => (
              <ActionItem key={action.id} action={action} platform={platform} onRun={runAction} />
            ))}
          </Command.Group>
        ))}
        {noteHits.length > 0 && (
          <Command.Group heading="Notes">
            {noteHits.map((hit) => (
              <ResultItem key={hit.id} kind="note" hit={hit} onOpen={openResult} />
            ))}
          </Command.Group>
        )}
        {taskHits.length > 0 && (
          <Command.Group heading="Tasks">
            {taskHits.map((hit) => (
              <ResultItem key={hit.id} kind="task" hit={hit} onOpen={openResult} />
            ))}
          </Command.Group>
        )}
      </Command.List>
      <div className="flex items-center gap-3 border-t border-border px-3 py-1.5 text-xs text-fg-faint">
        <span className="flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> navigate
        </span>
        <span className="flex items-center gap-1">
          <Kbd>↩</Kbd> select
        </span>
        <span className="flex items-center gap-1">
          <Kbd>esc</Kbd> close
        </span>
        <span className="ml-auto truncate">{HINT_TEXT}</span>
      </div>
    </Command.Root>
  );
}

/**
 * The command palette. Controlled: the owner holds `open`. Actions come from the registry; notes and
 * tasks come from the store's full-text search once the query has two characters. Selecting a
 * result runs the app's `notes.open` / `tasks.open` action with the id in the context.
 */
export function CommandPalette(props: CommandPaletteProps) {
  const { open, onOpenChange } = props;
  return (
    <Dialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      <Dialog.Content
        showClose={false}
        aria-label="Command palette"
        className="top-[18%] max-w-xl translate-y-0 gap-0 overflow-hidden p-0"
      >
        <PaletteBody {...props} />
      </Dialog.Content>
    </Dialog.Root>
  );
}

export type PaletteControls = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const RegistryContext = createContext<ActionRegistry | null>(null);
const PaletteContext = createContext<PaletteControls | null>(null);

export type CommandPaletteProviderProps = {
  registry: ActionRegistry;
  /** Read on every keypress and palette open; pass a stable function. */
  getContext: () => ActionContext;
  /** Turns global shortcuts off (e.g. while a modal owns the keyboard). Default true. */
  shortcutsEnabled?: boolean;
  platform?: Platform;
  children?: ReactNode;
};

/**
 * Owns the palette's open state, registers the built-in `palette.toggle` action with its configured shortcut,
 * installs the global shortcut listener, and renders the palette. Feature packages reach the
 * registry through `useActionRegistry()` and the palette through `usePalette()`.
 */
export function CommandPaletteProvider({
  registry,
  getContext,
  shortcutsEnabled = true,
  platform,
  children,
}: CommandPaletteProviderProps) {
  const [isOpen, setOpen] = useState(false);
  const shortcut = useShortcut(TOGGLE_ACTION);

  // The registry is an external system; registering into it is what effects are for.
  useEffect(
    () =>
      registry.register({
        id: TOGGLE_ACTION,
        title: "Command palette",
        group: "View",
        keywords: ["search", "commands", "actions"],
        ...(shortcut ? { shortcut } : {}),
        run: () => setOpen((o) => !o),
      }),
    [registry, shortcut],
  );

  const shortcutOptions =
    platform === undefined
      ? { enabled: shortcutsEnabled }
      : { enabled: shortcutsEnabled, platform };
  useGlobalShortcuts(registry, getContext, shortcutOptions);

  const controls: PaletteControls = {
    isOpen,
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen((o) => !o),
  };

  const paletteProps: CommandPaletteProps = {
    registry,
    getContext,
    open: isOpen,
    onOpenChange: setOpen,
  };
  if (platform !== undefined) paletteProps.platform = platform;

  return (
    <RegistryContext.Provider value={registry}>
      <PaletteContext.Provider value={controls}>
        {children}
        <CommandPalette {...paletteProps} />
      </PaletteContext.Provider>
    </RegistryContext.Provider>
  );
}

export function usePalette(): PaletteControls {
  const controls = useContext(PaletteContext);
  if (controls === null) throw new Error("usePalette must be used inside CommandPaletteProvider");
  return controls;
}

export function useActionRegistry(): ActionRegistry {
  const registry = useContext(RegistryContext);
  if (registry === null) {
    throw new Error("useActionRegistry must be used inside CommandPaletteProvider");
  }
  return registry;
}
